import React from 'react';
import {
    Alert,
    Modal,
    ModalHeader,
    ModalBody,
    Button,
    FormGroup,
    Col,
    ModalFooter,
    ListGroup,
    ListGroupItem,
    Spinner,
    Row,
} from 'reactstrap';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
    convertTimezone, convertWinToIANA, getTimeZoneAbbr, getWeekStartDate, resolveJobTimeZone,
} from '@s20/common/util/date';
import { getCounty } from '@s20/common/util/location';
import { clone as cloneUtil } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import AddressFormNew from '../address/AddressFormNew';
import { Address } from '../address/Address';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { BaseFormViewModel } from '../common/ViewModel';
import { Job } from '../job/Job';
import { getDuplicateJobs, showDuplicateJobsModal } from '../job/common/DuplicateJobDetection';
import CommonContext, {
    ApiRoutes, ChargeTypeResourceTypes, SupportedFileExtensionString, ServiceResponseResult,
    resourceHasRequestedQuantity, resourceHasChargeTypeChoice,
} from '../Common';
import {
    Textarea, toasty, onFieldChange, onReactSelectChanged,
    FormDivider, FormGroupColumn, FormLabel,
    Input,
    FormCheckbox,
} from '../common/forms/FormElements';
import FileUpload from '../common/forms/FileUpload';
import { SaveButton } from '../common/forms/SaveButton';
import { DEFAULT_DATETIME_FORMAT } from '../common/util';
import { OrganizationContact } from '../organization/Organization';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import { getCompanyContactsOptions } from '../contact/Contact';
import { ConfirmDelete, Modal as CommonModal } from '../common/Modal';
import { changedRunFields } from './JobScheduler/runApply';
import LocationSelector from './LocationSelector';
import { ResourcePanel } from './ResourcePanel';
import './JobForm.scss';

// Check if a resource has any assignment on any day.
const resourceHasAnyAssignments = (resource) =>
    Boolean(resource && ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
        .some((day) => Number(resource[day]) > 0));


// Check if a resource is flagged as an AFAD resource.
const resourceIsAfad = (resource) => Boolean(
    resource && resource.isAFAD,
);

const defaultEdit = {
    dayStatuses: {},
    resourceErrors: {},
    initialResources: null,
    allowRequestedBy: true,
    allowWeekOfDate: true,
    allowForemen: true,
    allowDispatchOfficeLocation: true,
    allowJobType: true,
    allowStartTime: true,
    allowWorkOrderNumber: true,
    allowResources: true,
    allowMeetingLocation: true,
    allowJobLocation: true,
    allowNotes: true,
    allowDelete: false,
    allowSubcontractor: true,
    allowProjectManagers: true,
    allowInspectorName: true,
};

const jobHasNotifyableChanges = (currentJob, originalJob) => {
    const locationChanged =
        currentJob.jobLocation?.address1 !== originalJob.jobLocation?.address1 ||
        currentJob.jobLocation?.city !== originalJob.jobLocation?.city ||
        currentJob.jobLocation?.state !== originalJob.jobLocation?.state ||
        currentJob.jobLocation?.zip !== originalJob.jobLocation?.zip;
    const meetingLocationChanged =
        currentJob.meetingLocation?.address1 !== originalJob.meetingLocation?.address1 ||
        currentJob.meetingLocation?.city !== originalJob.meetingLocation?.city ||
        currentJob.meetingLocation?.state !== originalJob.meetingLocation?.state ||
        currentJob.meetingLocation?.zip !== originalJob.meetingLocation?.zip;
    const startTimeChanged = currentJob.startTime !== originalJob.startTime;
    return locationChanged || meetingLocationChanged || startTimeChanged;
};

const fetchAndConfirmNotifications = async (jobIds) => {
    const assignments = await httpClient.post(
        ApiRoutes.job.confirmedAssignments(),
        jobIds,
        httpClient.format.json,
    );
    if (!assignments?.length) return { proceed: true, assignmentIds: [] };

    const confirmed = await CommonModal.ask({
        title: 'Notify Employees',
        SaveButtonProps: { label: 'Save and Notify' },
        body: () => (
            <>
                <Alert color="info">
                    The following confirmed employees will be notified and will need to re-confirm:
                </Alert>
                <ListGroup flush>
                    {assignments.map((a) => (
                        <ListGroupItem key={a.id}>
                            {a.employeeName}
                            {' — '}
                            {moment(a.assignmentDate, 'M/D/YY').format('ddd, MMM D, YYYY')}
                        </ListGroupItem>
                    ))}
                </ListGroup>
            </>
        ),
    });

    return { proceed: confirmed, assignmentIds: assignments.map((a) => a.id) };
};

class JobForm extends React.Component {
    static contextType = CommonContext;
    constructor(props) {
        super(props);

        this.state = {
            job: new Job(),
            usCounties: [],
            customerSubcontractors: [],
            tableRequired: {},
            formState: new BaseFormViewModel(),
            edit: defaultEdit,
            selectedAddressType: null,
            uploadSessionId: null,
            pageModified: false,
            originalJob: null,
            showApplyModal: false,
            pendingUpdatedJob: null,
            applyTargets: [],
            applyingSingleJob: false,
            applyingAllJobs: false,
            selectedContact: null,
            selectedContactIdProp: null,
            showContactsForm: null,
            allowContactTypeEdit: null,
            dispatchPortLoading: false,
            dispatchPorts: [],
        };


        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onDispatchOfficeChanged = this.onDispatchOfficeChanged.bind(this);
        this.onSaveJob = this.onSaveJob.bind(this);
        this.onDeleteConfirmed = this.onDeleteConfirmed.bind(this);
        this.setPageModified = this.setPageModified.bind(this);

        this.addressFormRef = React.createRef();
        this.formRef = React.createRef();
        this.contactFormRef = React.createRef();
    }

    setPageModified(flag) {
        this.setState({ pageModified: flag });
    }

    isQuickRentalContract() {
        return Boolean(this.props.contract?.isQuickRentalContract
            || this.state.job?.contract?.isQuickRentalContract);
    }

    onSelectChanged = onReactSelectChanged;
    onChange = onFieldChange;
    onDispatchOfficeChanged(selection, meta = {}) {
        this.onSelectChanged(selection, meta);
        this.fetchDispatchPorts(selection?.value ?? null);
    }

    fetchDispatchPorts = async (officeId) => {
        if (!officeId) {
            this.setState({ dispatchPorts: [], dispatchPortLoading: false });
            return;
        }
        this.setState({ dispatchPortLoading: true });
        const ports = await httpClient.js(ApiRoutes.typeAheads.dispatchPorts(officeId));
        const defaultPort = ports?.find((p) => p.isDefault) ?? null;
        this.setState((state) => {
            const portExists = ports?.some((p) => p.value === state.job.dispatchPortId);
            return {
                dispatchPorts: ports ?? [],
                dispatchPortLoading: false,
                job: portExists ? state.job : { ...state.job, dispatchPortId: defaultPort?.value ?? null },
            };
        });
    };

    onIsEmergencyChange = () => {
        const job = { ...this.state.job };
        job.isEmergency = !job.isEmergency;
        this.setState({ job });
    };

    resetForm = () => {
        const { formState } = this.state;

        const newJob = new Job();

        this.setState({
            formState: {
                ...formState, formValidated: false, errors: {}, saving: false,
            },
            tableRequired: {},
            uploadSessionId: null,
            job: newJob,
        });
    };

    componentDidMount() {
        if (this.props.slideOpen) {
            this.populateState();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.slideOpen && !prevProps.slideOpen) {
            void this.populateState();
        }
    }

    populateState = async () => {
        const {
            selectedJob, project, contract, dispatchLocations, customerSubcontractors, contractResources,
        } = this.props;
        const { formState } = this.state;
        try {
            this.setState({ formState: { ...formState, loading: true } });

            let fetchedJob;
            let jobManage = {};
            let initialEdit = { ...defaultEdit };

            if (selectedJob && selectedJob.id && typeof selectedJob.id !== 'string') {
                jobManage = await httpClient.js(ApiRoutes.job.byId(selectedJob.id));
                fetchedJob = selectedJob.isUpdated ? selectedJob : jobManage.job;

                // Preserve the original date when formatting startTime
                if (fetchedJob.startTime) {
                    const startDate = moment(fetchedJob.jobDate || fetchedJob.startTimeOffset);
                    fetchedJob.jobDate = startDate.format(DEFAULT_DATETIME_FORMAT);
                }

                fetchedJob.resources = fetchedJob.resources.map((resource) => {
                    const rentalContractResource = contractResources.find((cr) =>
                        !!cr.equipmentTypeId
                        && cr.equipmentTypeId === resource.equipmentTypeId
                        && cr.jobResourceTypeId === resource.jobResourceTypeId
                        && cr.jobResourceTypeId === ChargeTypeResourceTypes.Rental,
                    );

                    return {
                        ...resource,
                        rentalUnitTypeId: rentalContractResource?.rentalUnitTypeId ?? null,
                    };
                });
            } else if (selectedJob && typeof selectedJob.id === 'string') {
                fetchedJob = selectedJob;
                initialEdit = {
                    ...initialEdit,
                    allowDelete: true,
                };

                fetchedJob.tempSessionId = fetchedJob.id;

                // Normalize startTime to HH:mm if it’s an ISO string, or use default
                if (fetchedJob.startTime && fetchedJob.startTime.includes('T')) {
                    const tz = convertWinToIANA(fetchedJob.jobLocation.timeZone);

                    const start = moment(fetchedJob.startTimeOffset);
                    const date = moment()
                        .set({
                            hour: start.hour(),
                            minute: start.minute(),
                            second: 0,
                        })
                        .tz(tz);

                    fetchedJob.startTime = `${String(date.hours()).padStart(2, '0')}:${String(date.minutes()).padStart(2, '0')}`;
                } else {
                    fetchedJob.startTime = fetchedJob.startTime || '';
                }
            } else {
                // New job scenario, inherit from project
                fetchedJob = selectedJob || new Job();
                fetchedJob.id = uuid();
                fetchedJob.tempSessionId = fetchedJob.id;

                if (project && project.contractId) {
                    fetchedJob.contract = contract;
                    fetchedJob.contractId = contract.id;
                    fetchedJob.description = contract.description;
                    fetchedJob.resources = (contractResources || []).map((r) => ({
                        ...r,
                        id: r.id || uuid(),
                    }));
                    // Inherit additional fields from project
                    fetchedJob.foremenId = project.foremanId || null;
                    fetchedJob.requestedById = project.requestedById || null;
                    fetchedJob.projectManagerId = project.projectManagerId || null;
                    fetchedJob.projectName = project.projectName || null;
                    fetchedJob.workOrderNumber = project.workOrderNumber || '';
                    fetchedJob.workRequest = project.workRequest || '';
                    fetchedJob.purchaseOrderNumber = project.purchaseOrderNumber || '';
                    fetchedJob.dispatchOfficeLocationId = project.dispatchCompanyId || null;
                    fetchedJob.dispatchPortId = project.dispatchPortId || null;
                    fetchedJob.meetingLocation = project.meetingLocation || null;
                    fetchedJob.jobLocation = project.jobLocation || null;
                    fetchedJob.notes = project.notes || null;
                    fetchedJob.inspectorName = project.inspectorName || '';
                    fetchedJob.startTime = project.startTime || '';
                    fetchedJob.jobTypeId = project.jobTypeId || null;
                }
                if (dispatchLocations && dispatchLocations.length === 1) {
                    fetchedJob.dispatchOfficeLocationId = dispatchLocations[0].value;
                }
            }
            if (Array.isArray(fetchedJob.resources)) {
                fetchedJob.resources = fetchedJob.resources.map((r) => ({
                    ...r,
                    id: r.id || uuid(),
                }));
            }

            const dispatchPorts = fetchedJob.dispatchOfficeLocationId
                ? await httpClient.js(ApiRoutes.typeAheads.dispatchPorts(fetchedJob.dispatchOfficeLocationId))
                : [];
            const defaultPort = dispatchPorts?.find((p) => p.isDefault) ?? null;
            if (fetchedJob.dispatchPortId != null && !dispatchPorts?.some((p) => p.value === fetchedJob.dispatchPortId)) {
                fetchedJob.dispatchPortId = defaultPort?.value ?? null;
            }

            const isQuickRental = Boolean(contract?.isQuickRentalContract
                || fetchedJob.contract?.isQuickRentalContract);

            if (isQuickRental && !fetchedJob.meetingLocation && fetchedJob.dispatchOfficeLocationId) {
                const dispatchLoc = (this.props.dispatchLocations || []).find(
                    (d) => d.value === fetchedJob.dispatchOfficeLocationId,
                );
                if (dispatchLoc) {
                    const dispatchAddress = Object.assign(new Address(), {
                        address1: dispatchLoc.address1 ?? '',
                        city: dispatchLoc.city ?? '',
                        state: dispatchLoc.state ?? '',
                        zip: dispatchLoc.zip ?? '',
                        countryCode: dispatchLoc.countryCode ?? '',
                        usStateId: dispatchLoc.uSStateId ?? null,
                        countryId: dispatchLoc.countryId ?? null,
                        timeZone: dispatchLoc.timeZone ?? null,
                        usCountyId: dispatchLoc.uSCountyId ?? null,
                        latitude: dispatchLoc.latitude ?? null,
                        longitude: dispatchLoc.longitude ?? null,
                    });
                    fetchedJob.meetingLocation = dispatchAddress;
                    fetchedJob.jobLocation = dispatchAddress;
                }
            }

            this.setState({
                customerSubcontractors,
                job: fetchedJob,
                originalJob: cloneUtil(fetchedJob),
                uploadSessionId: fetchedJob.tempSessionId,
                edit: {
                    ...initialEdit,
                    ...jobManage.edit,
                    initialResources: fetchedJob.resources ? { ...fetchedJob.resources } : null,
                },
                formState: { ...formState, loading: false },
                dispatchPorts: dispatchPorts ?? [],
            });
        } catch (error) {
            console.error('Failed to fetch job data:', error);
            toasty.error('Error loading job data');
            this.setState({ formState: { ...formState, loading: false } });
        }
    };

    onSaveJob = async (event) => {
        // Prevent the default form submission behavior.
        event.preventDefault();

        //Use the native form validation
        if (!this.formRef.current.checkValidity()) {
            // This will display the browser's built-in validation errors.
            this.formRef.current.reportValidity();
            return;
        }

        const { job } = this.state;
        const { selectedDate, project } = this.props;

        if (!job) return;
        if (!selectedDate) {
            toasty.error('No selected date!');
            return;
        }

        if (!this.onValidate()) {
            toasty.error('Validation failed. Please fix the errors.');
            return;
        }

        const updatedJob = { ...job };
        const jobDate = updatedJob.jobDate || selectedDate;
        const groupingId = updatedJob.parentJobId || updatedJob.id;
        const editedDate = moment(jobDate).startOf('day');
        const runJobs = (this.props.project.jobs || []).filter((j) => (j.parentJobId || j.id) === groupingId);
        const futureRunJobs = runJobs.filter((j) => moment(j.jobDate).startOf('day').isSameOrAfter(editedDate)
            && j.id !== updatedJob.id
            && !j.hasSubmittedTimesheet);

        if (futureRunJobs.length > 0) {
            const currentForList = {
                id: updatedJob.id,
                jobDate,
            };

            this.setState({
                showApplyModal: true,
                pendingUpdatedJob: updatedJob,
                applyTargets: [currentForList, ...futureRunJobs],
            });

            return;
        }

        const timeZone = resolveJobTimeZone(updatedJob, this.props.dispatchLocations);

        if (typeof updatedJob.id === 'string') {
            // For new jobs
            updatedJob.tempSessionId = updatedJob.id;
            updatedJob.weekOfDate = getWeekStartDate(selectedDate);
            updatedJob.projectId = project.id;

            // Combine selectedDate and startTime
            if (updatedJob.startTime) {
                const [hours, minutes] = updatedJob.startTime.split(':').map(Number);
                const jobDateTime = new Date(selectedDate);
                jobDateTime.setHours(hours, minutes, 0, 0);
                updatedJob.startTime = moment(jobDateTime).format();
                updatedJob.startTimeOffset = timeZone
                    ? convertTimezone(jobDateTime, timeZone)
                    : moment(jobDateTime).format();
                updatedJob.jobDate = moment(jobDateTime).format();
            }
        } else {
            // For existing jobs
            updatedJob.tempSessionId = null;
            updatedJob.isUpdated = true;
            updatedJob.weekOfDate = updatedJob.weekOfDate || getWeekStartDate(selectedDate);
            const [hours, minutes] = updatedJob.startTime.split(':').map(Number);
            const jobDateTime = new Date(selectedDate);
            jobDateTime.setHours(hours, minutes, 0, 0);
            updatedJob.startTime = moment(jobDateTime).format();
            updatedJob.startTimeOffset = timeZone
                ? convertTimezone(jobDateTime, timeZone)
                : moment(jobDateTime).format();
        }

        // Clean up job locations (if the id is a string, we assume it's temporary and set it to null)
        updatedJob.meetingLocation = typeof updatedJob.meetingLocation?.id === 'string'
            ? { ...updatedJob.meetingLocation, id: null }
            : updatedJob.meetingLocation;
        updatedJob.jobLocation = typeof updatedJob.jobLocation?.id === 'string'
            ? { ...updatedJob.jobLocation, id: null }
            : updatedJob.jobLocation;

        if (project.id && updatedJob.jobLocation && !this.isQuickRentalContract()) {
            if (!updatedJob.id || typeof updatedJob.id === 'string')
                updatedJob.id = null;
            const duplicateJobsList = await getDuplicateJobs({
                duplicateJobRequests: [{
                    jobDate: moment(jobDate).toISOString(),
                    jobLocation: updatedJob.jobLocation,
                    excludeJobId: updatedJob.id ?? null,
                }],
            });
            if (duplicateJobsList.length > 0) {
                this.setState((prevState) => ({
                    formState: { ...prevState.formState, saving: false },
                }));
                const proceed = await showDuplicateJobsModal(duplicateJobsList);
                if (!proceed) return;
                this.setState((prevState) => ({
                    formState: { ...prevState.formState, saving: true },
                }));
            }
        }

        const { originalJob } = this.state;
        let assignmentIdsToNotify = [];
        if (originalJob && typeof updatedJob.id !== 'string' && updatedJob.id
            && jobHasNotifyableChanges(this.state.job, originalJob)) {
            const { proceed, assignmentIds } = await fetchAndConfirmNotifications(
                [updatedJob.id],
            );
            if (!proceed) {
                this.setState((prevState) => ({
                    formState: { ...prevState.formState, saving: false },
                }));
                return;
            }
            assignmentIdsToNotify = assignmentIds;
        }

        await this.proceedWithSave(updatedJob, assignmentIdsToNotify);
    };

    proceedWithSave = async (updatedJob, assignmentIdsToNotify = []) => {
        const { project, onJobSave, selectedDate } = this.props;

        if (project.id) {
            updatedJob.resources = updatedJob.resources.map((r) => ({
                ...r,
                id: typeof r.id === 'string' ? null : r.id,
                jobId: typeof updatedJob.id === 'string' ? 0 : updatedJob.id,
                rentalDropoffDate: r.rentalDropoffQuantity > 0 ? moment(selectedDate).format(DEFAULT_DATETIME_FORMAT) : null,
                rentalPickupDate: r.rentalPickupQuantity > 0 ? moment(selectedDate).format(DEFAULT_DATETIME_FORMAT) : null,
            }));

            try {
                const message = typeof updatedJob.id === 'string' ? 'added' : 'updated';
                await this.saveProjectJob(updatedJob, message);

                if (assignmentIdsToNotify.length > 0 && typeof updatedJob.id !== 'string') {
                    try {
                        await httpClient.post(
                            ApiRoutes.jobAssignment.notifyJobUpdate(),
                            assignmentIdsToNotify,
                            httpClient.format.json,
                        );
                    } catch (notifyError) {
                        console.warn('Job update notifications failed:', notifyError);
                        toasty.warning('Job saved, but employee notifications failed to send.');
                    }
                }
            } catch (error) {
                console.error('Error saving job:', error);
                toasty.error('Failed to save job.');
            }
        } else {
            onJobSave(updatedJob);
        }
        this.resetForm();
        this.props.setSlideOpen(false);
    };
    saveProjectJob = async (updatedJob, message) => {
        const { refreshProject } = this.props;
        let response;

        updatedJob.contract = null;

        if (!updatedJob.id || typeof updatedJob.id === 'string') {
            updatedJob.id = null;
            // Create a new job directly
            response = await httpClient.post(ApiRoutes.job.create(), updatedJob, httpClient.format.json);
        } else if (updatedJob.isDeleted) {
            response = await httpClient.delete(
                ApiRoutes.job.delete(updatedJob.id),
            );
        } else {
            // Update existing job directly1
            response = await httpClient.put(ApiRoutes.job.update(updatedJob.id), updatedJob, httpClient.format.json);
        }

        if (response.error === 0) {
            if (response.result === ServiceResponseResult.OkWithErrors) {
                toasty.warning('Job Saved', response.message || 'Job saved, but employee notifications failed to send.');
            } else {
                toasty.success(`Job ${message} successfully!`);
            }
            if (refreshProject) {
                await refreshProject();
            }
        } else {
            throw new Error(response.message || 'Failed to save job.');
        }
    };

    applyOnlyThisJob = async () => {
        this.setState({ applyingSingleJob: true });

        const { project, onJobSave, refreshProject } = this.props;
        const pending = this.state.pendingUpdatedJob || this.state.job;
        const startTime = pending.startTime ? moment(pending.jobDate)
            .set({
                hours: moment(pending.startTime, 'HH:mm').hours(),
                minutes: moment(pending.startTime, 'HH:mm').minutes(),
                seconds: 0,
                milliseconds: 0,
            })
            .format() : pending.startTime;
        let saved = false;
        try {
            const timeZone = resolveJobTimeZone(pending, this.props.dispatchLocations);
            const updatedJob = {
                ...pending,
                startTime,
                startTimeOffset: timeZone
                    ? convertTimezone(startTime, timeZone)
                    : moment(startTime).format(),
            };

            if (project.id) {
                const message = typeof updatedJob.id === 'string' ? 'added' : 'updated';

                let assignmentIds = [];
                if (typeof updatedJob.id !== 'string') {
                    const { proceed, assignmentIds: ids } = await fetchAndConfirmNotifications(
                        [updatedJob.id],
                    );
                    if (!proceed) return;
                    assignmentIds = ids;
                }

                await this.saveProjectJob(updatedJob, message);
                saved = true;

                if (assignmentIds.length > 0) {
                    try {
                        await httpClient.post(
                            ApiRoutes.jobAssignment.notifyJobUpdate(),
                            assignmentIds,
                            httpClient.format.json,
                        );
                    } catch (notifyError) {
                        console.warn('Job update notifications failed:', notifyError);
                        toasty.warning('Job saved, but employee notifications failed to send.');
                    }
                }

                await refreshProject();
            } else {
                onJobSave(updatedJob);
                saved = true;
            }

            toasty.success('Job saved.');
        } catch (e) {
            console.error(e);
            toasty.error('Failed to save job.');
        } finally {
            if (saved) {
                this.setState({
                    showApplyModal: false, pendingUpdatedJob: null, applyTargets: [], applyingSingleJob: false,
                });
                this.resetForm();
                this.props.setSlideOpen(false);
            } else {
                this.setState({ applyingSingleJob: false });
            }
        }
    };

    applyAllJobsInRun = async () => {
        this.setState({ applyingAllJobs: true });

        const { onJobsBulkUpdate } = this.props;
        const updatedJob = this.state.pendingUpdatedJob || this.state.job;
        // Only what the dispatcher changed in this edit is carried onto the rest of the run.
        const changedFields = changedRunFields(updatedJob, this.state.originalJob);

        let saved = false;
        try {
            const runJobIds = (this.state.applyTargets ?? [])
                .filter((t) => typeof t.id !== 'string')
                .map((t) => t.id);

            if (runJobIds.length > 0) {
                const { proceed, assignmentIds } = await fetchAndConfirmNotifications(runJobIds);
                if (!proceed) return;

                await onJobsBulkUpdate(updatedJob, changedFields);
                saved = true;

                if (assignmentIds.length > 0) {
                    try {
                        await httpClient.post(
                            ApiRoutes.jobAssignment.notifyJobUpdate(),
                            assignmentIds,
                            httpClient.format.json,
                        );
                    } catch (notifyError) {
                        console.warn('Job update notifications failed:', notifyError);
                        toasty.warning('Jobs saved, but employee notifications failed to send.');
                    }
                }
            } else {
                await onJobsBulkUpdate(updatedJob, changedFields);
                saved = true;
            }
        } catch (error) {
            console.error('Failed to update jobs:', error);
            if (error?.handledError?.message) {
                toasty.error('Failed to update jobs in this run.', error.handledError.message);
            } else {
                toasty.error('Failed to update jobs in this run.');
            }
        } finally {
            if (saved) {
                this.setState({
                    showApplyModal: false,
                    pendingUpdatedJob: null,
                    applyTargets: [],
                    applyingAllJobs: false,
                });
                this.resetForm();
                this.props.setSlideOpen(false);
            } else {
                this.setState({ applyingAllJobs: false });
            }
        }
    };

    onValidate = () => {
        let valid = true;
        const errors = {};
        const requiredStyle = {
            border: '1px solid',
            borderRadius: '0.25rem',
            borderColor: '#ed1c24',
        };

        const hasAssignedResources = this.state.job.resources?.some((resource) =>
            resourceHasAnyAssignments(resource),
        );

        if (!hasAssignedResources) {
            valid = false;
            errors.resources = 'At least one resource with a non-zero quantity is required.';
            this.setState({ tableRequired: requiredStyle });
        }

        if (hasAssignedResources && !this.isQuickRentalContract()) {
            const hasEmployees = this.state.job.resources?.some(
                (r) => r.jobResourceTypeId === ChargeTypeResourceTypes.Employee
                    && resourceHasAnyAssignments(r),
            );
            if (!hasEmployees) {
                valid = false;
                errors.resources = 'At least one employee resource is required.';
                this.setState({ tableRequired: requiredStyle });
            }
        }

        if (this.props.contract?.setupChargesRequired) {
            // Setup charges are employee labour-related; only validate when the job has employees.
            const hasEmployees = this.state.job.resources?.some(
                (r) => r.jobResourceTypeId === ChargeTypeResourceTypes.Employee
                    && resourceHasAnyAssignments(r),
            );
            if (hasEmployees) {
                const hasSetupCharge = this.state.job.resources
                    ?.filter((r) => r.jobResourceTypeId === ChargeTypeResourceTypes.Setup && r.chargeTypeId > 0)
                    .some((resource) => resourceHasAnyAssignments(resource));
                if (!hasSetupCharge) {
                    valid = false;
                    errors.setupCharge = 'At least one setup charge is required.';
                }
            }
        }

        if (!this.isQuickRentalContract()) {
            if (!this.state.job.meetingLocation) {
                valid = false;
                errors.meetingLocation = 'Meeting Location is required.';
            }
            if (!this.state.job.jobLocation) {
                valid = false;
                errors.jobLocation = 'Job Location is required.';
            }
        }
        if (!this.state.job.startTime) {
            valid = false;
            errors.startTime = 'Start Time is required.';
        }

        if (this.props.contract.isProjectContract) {
            const missingDates = this.state.job.resources
                .filter((r) => r.jobResourceTypeId === ChargeTypeResourceTypes.Rental)
                .filter((r) => {
                    const dropoff = (r.rentalDropoffQuantity || 0) > 0;
                    const pickup = (r.rentalPickupQuantity || 0) > 0;
                    return (dropoff && !r.rentalStartDate) || (pickup && !r.rentalEndDate);
                });

            if (missingDates.length > 0) {
                valid = false;
                errors.resources = 'Please enter a rental start or rental end date for every allocated rental resource.';
                this.setState({ tableRequired: requiredStyle });
            }
        }

        const missingChargeTypes = (this.state.job.resources ?? [])
            .filter((r) => resourceHasChargeTypeChoice(r)
                && !r.chargeTypeOptions.some((o) => o.chargeTypeId === r.chargeTypeId)
                && resourceHasRequestedQuantity(r));

        if (missingChargeTypes.length > 0) {
            valid = false;
            errors.resources = `Select a charge type for: ${missingChargeTypes
                .map((r) => r.resourceName ?? r.equipmentTypeName)
                .join(', ')}.`;
            this.setState({ tableRequired: requiredStyle });
        }

        this.setState((prevState) => ({
            formState: {
                ...prevState.formState, formValidated: valid, errors, saving: valid,
            },
        }));
        return valid;
    };

    onSaveAddress = (address) => {
        const { selectedAddressType, job } = this.state;
        const { usCounties } = this.props;
        const isQuickRental = this.isQuickRentalContract();

        if (!selectedAddressType) return;

        const countyData = getCounty(usCounties, address);
        const updatedJob = {
            ...job,
            [selectedAddressType]: { ...address, county: countyData },
        };

        if (address.duplicate) {
            if (selectedAddressType === 'jobLocation') {
                updatedJob.meetingLocation = {
                    ...address, id: updatedJob.meetingLocation?.id ?? uuid(), county: countyData,
                };
            } else if (selectedAddressType === 'meetingLocation') {
                updatedJob.jobLocation = {
                    ...address, id: updatedJob.jobLocation?.id ?? uuid(), county: countyData,
                };
            }
        }

        if (isQuickRental) {
            updatedJob.meetingLocation = { ...address, id: uuid(), county: countyData };
            updatedJob.jobLocation = { ...address, id: uuid(), county: countyData };
        }

        this.setState({ job: updatedJob, selectedAddressType: null });
    };

    handleCloseModal = () => {
        this.resetForm();
        this.props.setSlideOpen(false);
    };

    isRequestedPersonnelSectionAvailable() {
        const { job } = this.state;
        return (
            job?.resources &&
            job?.resources.some((r) => r.isPlanningOnly) &&
            job?.resources.some((r) => resourceIsAfad(r) && resourceHasAnyAssignments(r))
        );
    }

    /* File uploads */
    onRemoveAttachment = async (file) => {
        const { job } = { ...this.state };
        const url = ApiRoutes.job.attachment(job.id || this.state.uploadSessionId);
        const updatedUploads = job.uploads.filter((f) => f.name !== file.name);

        this.setState({
            job: {
                ...job,
                uploads: updatedUploads,
            },
        });

        try {
            const response = await httpClient.delete(url, file, httpClient.format.json);

            if (response) {
                toasty.success('Attachment removed.');
                // Fetch updated list to confirm the removal
                this.getAttachments();
            } else {
                const errorData = await response.json();
                toasty.error(errorData.message || 'Failed to remove the attachment. Try refreshing.');
            }
        } catch (error) {
            console.error(error);
            toasty.error('Could not delete attachment due to a server error.');
        }
    };

    onDownloadAttachment = async (file) => {
        const { job } = { ...this.state };
        if (!file) return;

        const url = ApiRoutes.job.attachment(job.id || this.state.uploadSessionId);
        await httpClient.downloadFile(url, file, file.name);
    };

    getAttachments = async () => {
        const { job } = { ...this.state };
        const jobIdOrSessionId = job.id ? job.id : this.state.uploadSessionId;
        const attachments = await httpClient.get(
            ApiRoutes.job.getAllAttachments(jobIdOrSessionId),
        );

        this.setState({
            job: {
                ...job,
                uploads: attachments ?? [],
            },
        });
    };

    getDefaultRentalDate() {
        const { selectedDate } = this.props;
        const { job: { startTime } } = this.state;
        const m = moment(selectedDate).clone();

        if (startTime) {
            const [hours, minutes] = startTime.split(':').map(Number);
            if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                m.set({ hour: hours, minute: minutes });
            } else {
                const parsed = moment(startTime);
                if (parsed.isValid()) {
                    m.set({ hour: parsed.hour(), minute: parsed.minute() });
                }
            }
        } else {
            m.startOf('day');
        }

        return m.format(DEFAULT_DATETIME_FORMAT);
    }

    onDeleteConfirmed = async () => {
        const { job } = this.state;

        try {
            await this.props.onDeleteJob(job);
        } catch (error) {
            console.error('Error deleting job:', error);
            toasty.error('Failed to delete job.');
        } finally {
            this.resetForm();
            this.props.setSlideOpen(false);
        }
    };

    getTzAbbreviation = () => {
        const { job } = this.state;
        if (!job) return '';
        return getTimeZoneAbbr(job.startTimeOffset, job.jobLocation?.timeZone, job.jobDate, job.startTime);
    };

    onAddContact = (prop) => {
        const { contract, setSlideOpen } = this.props;

        if (!contract) return;

        if (setSlideOpen) {
            setSlideOpen(true);
        }

        if (this.contactFormRef.current) {
            this.contactFormRef.current.resetForm();
        }
        const newContact = new OrganizationContact();
        newContact.id = null;
        newContact.companyId = contract.companyId;

        this.setState({
            selectedContact: newContact,
            selectedContactIdProp: prop,
            showContactsForm: true,
            allowContactTypeEdit: true,
            contactFormIsReadOnly: false,
        });
    };

    onEditContact = async (prop, readOnly = false) => {
        const { job } = this.state;
        const { setSlideOpen } = this.props;

        if (!job[prop]) return;

        if (setSlideOpen) {
            setSlideOpen(true);
        }

        try {
            const selectedContactData = await httpClient.js(ApiRoutes.locationContact.byId(job[prop]));
            if (this.contactFormRef.current) {
                this.contactFormRef.current.resetForm();
            }

            this.setState({
                selectedContact: selectedContactData,
                selectedContactIdProp: prop,
                showContactsForm: true,
                allowContactTypeEdit: false,
                contactFormIsReadOnly: readOnly,
            });
        } catch (error) {
            console.error('Error fetching contact:', error);
        }
    };

    onOrganizationContactSaved = async () => {
        const { selectedContact, selectedContactIdProp } = this.state;
        const { contract } = this.props;

        if (!selectedContactIdProp || !selectedContact) return;

        this.setState({ contactFormIsReadOnly: true });
        const isNew = !selectedContact.id;

        try {
            const result = await httpClient.andGetResponse(
                isNew ? httpClient.types.post : httpClient.types.put,
                isNew ? ApiRoutes.locationContact.create() : ApiRoutes.locationContact.update(selectedContact.id),
                selectedContact,
            );
            if (result) {
                toasty.success('Contact saved successfully.');

                const updatedJob = { ...this.state.job, [selectedContactIdProp]: result.id ?? result };
                this.setState({ job: updatedJob });

                const companyContacts = await getCompanyContactsOptions(contract.companyId);
                this.props.onUpdateContacts(companyContacts);

                this.setState({ showContactsForm: false });
            }
        } catch (error) {
            console.error('Error saving contact:', error);
        } finally {
            this.setState({ contactFormIsReadOnly: false });
        }
    };

    getContactName = (contactId) => {
        const { contactsOptions } = this.props;

        return contactsOptions?.companyContactsOptions?.find((x) => x.value === contactId)?.label || 'Unknown';
    };

    onDeleteButtonClick = () => {
        ConfirmDelete.show({
            title: 'Delete Job',
            onSubmit: this.onDeleteConfirmed,
            body: () => 'Are you sure you want to delete this job?',
        });
    };


    render() {
        const {
            formState, job, allowContactTypeEdit, edit, customerSubcontractors,
            showContactsForm, contactFormIsReadOnly, selectedContact, dispatchPortLoading, dispatchPorts,
        } = this.state;

        const {
            slideOpen, selectedDate, contactsOptions, dispatchLocations,
            selectedJob, resourceKits, jobTypesOptions, viewOnly,
            usCounties, contract, project, isRentalContract,
        } = this.props;

        const { tenantSettings } = this.context.tenant;
        const selectedDispatchLocation = (dispatchLocations || []).find(
            (d) => d.value === job?.dispatchOfficeLocationId,
        );
        const isDispatchCancelled = (job?.dateCancelled || []).some((cancelDate) =>
            moment(cancelDate).isSame(moment(selectedDate), 'day'),
        );
        const isDisabled = Boolean(viewOnly) || isDispatchCancelled;

        return (
            <>
                <Modal
                    isOpen={slideOpen}
                    toggle={this.handleCloseModal}
                    size="lg"
                >
                    <ModalHeader toggle={this.handleCloseModal}>
                        {typeof selectedJob?.id === 'string' ? 'Create Job' : 'Edit Job'} {' '}
                        {selectedDate && job?.contractSequence ? `(${job.jobNumber}) on ${selectedDate.toLocaleDateString()}` : `${selectedDate?.toLocaleDateString()}`}
                    </ModalHeader>
                    <ModalBody>
                        {formState.loading || !tenantSettings ? (
                            <div
                                className="d-flex justify-content-center align-items-center"
                                style={{ minHeight: '200px' }}
                            >
                                <Spinner />
                            </div>
                        ) : (
                            <form
                                ref={this.formRef}
                                onSubmit={this.onSaveJob}
                                noValidate
                            >
                                <fieldset
                                    className="flex-fill overflow-x-hidden container opacity-1 position-relative mb-2"
                                    disabled={isDisabled}
                                >
                                    <Row>
                                        <FormGroupColumn>
                                            <ValidatedSelect
                                                id="requestedById"
                                                name="job.requestedById"
                                                label="Requested By"
                                                options={contactsOptions?.companyContactsOptions}
                                                value={(contactsOptions?.companyContactsOptions || []).find(
                                                    (x) => x.value === job?.requestedById,
                                                ) || ''}
                                                onChange={this.onSelectChanged}
                                                required
                                                error="A requested-by selection is required."
                                                isDisabled={!edit.allowRequestedBy}
                                                showAddButton={!job?.hasAssignments}
                                                onAddButtonClick={() => this.onAddContact(
                                                    'requestedById',
                                                )}
                                                showEditButton={!!job?.requestedById}
                                                onEditButtonClick={() => this.onEditContact(
                                                    'requestedById',
                                                )}
                                                showViewButton={!!job?.requestedById}
                                                onViewButtonClick={() => this.onEditContact(
                                                    'requestedById', true,
                                                )}
                                            />
                                        </FormGroupColumn>

                                        <FormGroupColumn>
                                            <ValidatedSelect
                                                id="dispatchOfficeLocationId"
                                                name="job.dispatchOfficeLocationId"
                                                label="Dispatching"
                                                options={dispatchLocations || []}
                                                value={selectedDispatchLocation || ''}
                                                onChange={this.onDispatchOfficeChanged}
                                                required
                                                error="A dispatch selection is required."
                                                isDisabled={!edit.allowDispatchOfficeLocation}
                                            />
                                            {!!job.dispatchOfficeLocationId && !!dispatchPorts.length && (
                                                <ValidatedSelect
                                                    id="dispatchPortId"
                                                    name="job.dispatchPortId"
                                                    options={dispatchPorts}
                                                    label="Dispatch Port"
                                                    placeholder="Use office location"
                                                    value={dispatchPorts.find((o) => o.value === job.dispatchPortId) ?? null}
                                                    onChange={this.onSelectChanged}
                                                    isLoading={dispatchPortLoading}
                                                    disabled={dispatchPortLoading}
                                                />
                                            )}
                                        </FormGroupColumn>
                                    </Row>

                                    <Row>
                                        <FormGroupColumn>
                                            <ValidatedSelect
                                                id="foremenId"
                                                name="job.foremenId"
                                                label="Foreman"
                                                options={contactsOptions?.companyContactsOptions}
                                                value={(contactsOptions?.companyContactsOptions || []).find(
                                                    (x) => x.value === job?.foremenId,
                                                ) || ''}
                                                onChange={this.onSelectChanged}
                                                required
                                                error="A foreman selection is required."
                                                isDisabled={!edit.allowForemen}
                                                showAddButton={!job?.hasAssignments}
                                                onAddButtonClick={() => this.onAddContact(
                                                    'foremenId',
                                                )}
                                                showEditButton={!!job?.foremenId}
                                                onEditButtonClick={() => this.onEditContact(
                                                    'foremenId', !edit.allowForemen,
                                                )}
                                                showViewButton={!!job?.foremenId}
                                                onViewButtonClick={() => this.onEditContact(
                                                    'foremenId', true,
                                                )}
                                            />
                                        </FormGroupColumn>

                                        {contract.workOrderTypeId !== 3 && (
                                            <FormGroupColumn>
                                                <Input
                                                    label="Work Order #"
                                                    id="workOrderNumber"
                                                    name="job.workOrderNumber"
                                                    value={
                                                        job.workOrderNumber ?? ''
                                                    }
                                                    onChange={this.onChange}
                                                    readOnly={
                                                        !edit.allowWorkOrderNumber
                                                    }
                                                    maxLength={44}
                                                />
                                            </FormGroupColumn>
                                        )}
                                        {contract?.companyRequiresWorkRequest && (
                                            <FormGroupColumn>
                                                <Input
                                                    label="Work Request"
                                                    id="workRequest"
                                                    name="job.workRequest"
                                                    value={job.workRequest ?? ''}
                                                    onChange={this.onChange}
                                                    maxLength={44}
                                                />
                                            </FormGroupColumn>
                                        )}
                                        {contract?.companyRequiresPurchaseOrderNumber && (
                                            <FormGroupColumn>
                                                <Input
                                                    label="PO Number"
                                                    id="purchaseOrderNumber"
                                                    name="job.purchaseOrderNumber"
                                                    value={job.purchaseOrderNumber ?? ''}
                                                    onChange={this.onChange}
                                                    maxLength={44}
                                                />
                                            </FormGroupColumn>
                                        )}
                                    </Row>

                                    {(tenantSettings?.contractsCanEnforceProjectManagers && (contract ?? {}).requireProjectInformation) && (
                                        <Row>
                                            <FormGroupColumn>
                                                <Input
                                                    label="Project Name"
                                                    id="projectName"
                                                    name="job.projectName"
                                                    value={job.projectName ?? ''}
                                                    onChange={this.onChange}
                                                    error="Project Name is required."
                                                    required
                                                />
                                            </FormGroupColumn>
                                            <FormGroupColumn>
                                                <ValidatedSelect
                                                    id="projectManager"
                                                    name="job.projectManagerId"
                                                    label="Project Manager"
                                                    options={contactsOptions?.projectManagerContactOptions || []}
                                                    value={(contactsOptions?.projectManagerContactOptions || []).find(
                                                        (x) => x.value === job?.projectManagerId,
                                                    ) || ''}
                                                    onChange={this.onSelectChanged}
                                                    required
                                                    error="A Project Manager selection is required."
                                                    isDisabled={!edit.allowProjectManagers}
                                                    showAddButton
                                                    onAddButtonClick={() => this.onAddContact(
                                                        'projectManagerId',
                                                    )}
                                                    showEditButton={!!job?.projectManagerId}
                                                    onEditButtonClick={() => this.onEditContact(
                                                        'projectManagerId',
                                                    )}
                                                    showViewButton={!!job?.projectManagerId}
                                                    onViewButtonClick={() => this.onEditContact(
                                                        'projectManagerId', true,
                                                    )}
                                                />
                                            </FormGroupColumn>
                                        </Row>
                                    )}

                                    <Row>
                                        {job?.contract?.invoiceHasInspectorName && (
                                            <FormGroupColumn>
                                                <Input
                                                    id="inspectorName"
                                                    name="job.inspectorName"
                                                    label="Inspector Name"
                                                    value={
                                                        job.inspectorName ?? ''
                                                    }
                                                    onChange={this.onChange}
                                                    readOnly={
                                                        !edit.allowInspectorName
                                                    }
                                                />
                                            </FormGroupColumn>
                                        )}
                                        {job?.contract?.subcontractorsApply && (
                                            <FormGroupColumn>
                                                <ValidatedSelect
                                                    id="subcontractorCompanyId"
                                                    name="job.subcontractorCompanyId"
                                                    label="Subcontractor"
                                                    options={customerSubcontractors || []}
                                                    value={(customerSubcontractors || []).find(
                                                        (x) => x.value === job?.subcontractorCompanyId,
                                                    ) || ''}
                                                    onChange={this.onSelectChanged}
                                                    required
                                                    error="A subcontractor selection is required."
                                                />
                                            </FormGroupColumn>
                                        )}
                                    </Row>

                                    <Row>
                                        <FormGroupColumn>
                                            <FormGroup className="mb-1">
                                                <FormCheckbox
                                                    id="isEmergency"
                                                    name="isEmergency"
                                                    checked={job.isEmergency ?? false}
                                                    onChange={this.onIsEmergencyChange}
                                                    labelText="This is an emergency job"
                                                />
                                            </FormGroup>
                                        </FormGroupColumn>
                                    </Row>

                                    <FormDivider />

                                    <Row>
                                        <FormGroupColumn>
                                            <Input
                                                label="Start Time"
                                                id="startTime"
                                                name="job.startTime"
                                                type="time"
                                                value={job?.startTime || ''}
                                                onChange={this.onChange}
                                                required
                                                disabled={!edit.allowStartTime}
                                                error={formState.errors.startTime}
                                                endAddonClassName="inset"
                                                endAddon={(
                                                    <span className="text-muted">
                                                        {this.getTzAbbreviation()}
                                                    </span>
                                                )}
                                            />
                                        </FormGroupColumn>

                                        {job?.jobTypeId !== 1 && (
                                            <FormGroupColumn>
                                                <ValidatedSelect
                                                    id="jobType"
                                                    name="job.jobTypeId"
                                                    options={jobTypesOptions}
                                                    label="What type of work is being done?"
                                                    error="A job type must be selected"
                                                    required={(jobTypesOptions ?? []).some((x) => x.timesheetRequiresWorkflowNotes) && !this.isQuickRentalContract()}
                                                    value={
                                                        (jobTypesOptions ?? []).find(
                                                            (x) => x.value === job.jobTypeId,
                                                        ) ?? ''
                                                    }
                                                    onChange={this.onSelectChanged}
                                                />
                                            </FormGroupColumn>
                                        )}
                                    </Row>

                                    {/* Resource Panel Section */}
                                    <ResourcePanel
                                        job={job}
                                        contract={contract}
                                        selectedDate={selectedDate}
                                        edit={edit}
                                        project={project}
                                        errors={formState.errors}
                                        tableRequired={this.state.tableRequired}
                                        onJobChange={(nextJob) => this.setState({ job: nextJob })}
                                        selectedResourceKits={job.selectedResourceKits}
                                        resourceKits={resourceKits}
                                        isRentalContract={isRentalContract}
                                    />

                                    {/* Locations */}
                                    <Row>
                                        {!this.isQuickRentalContract() && (
                                            <FormGroupColumn>
                                                <LocationSelector
                                                    label="Meeting Location"
                                                    type="meetingLocation"
                                                    stateData={job}
                                                    contract={job?.contract}
                                                    usCounties={usCounties}
                                                    addressFormRef={this.addressFormRef}
                                                    setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                                    locationRequired={formState.errors.meetingLocation}
                                                    isDisabled={!edit.allowMeetingLocation}
                                                />
                                            </FormGroupColumn>
                                        )}
                                        <FormGroupColumn>
                                            <LocationSelector
                                                label="Job Location"
                                                type="jobLocation"
                                                stateData={job}
                                                contract={job?.contract}
                                                usCounties={usCounties}
                                                addressFormRef={this.addressFormRef}
                                                setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                                locationRequired={formState.errors.jobLocation}
                                                isDisabled={!edit.allowJobLocation}
                                                allowSameAs={!this.isQuickRentalContract()}
                                            />
                                        </FormGroupColumn>
                                    </Row>

                                    {/* Notes */}
                                    <Row>
                                        <Col>
                                            <Textarea
                                                label="Notes"
                                                name="job.notes"
                                                rows="5"
                                                maxLength={1000}
                                                value={job?.notes || ''}
                                                onChange={this.onChange}
                                                disabled={!edit.allowNotes}
                                            />
                                        </Col>
                                    </Row>

                                    {/* Job enabled Section */}
                                    {!!tenantSettings?.jobUploadsEnabled && (
                                        <Row>
                                            <Col>
                                                <FormGroup>
                                                    <FormLabel
                                                        text={(
                                                            <>
                                                                Attach Files
                                                                <small className="text-muted d-block mt-1">
                                                                    Max 40MB | Supported: {SupportedFileExtensionString}
                                                                </small>
                                                            </>
                                                        )}
                                                    />
                                                    <FileUpload
                                                        id="jobUploader"
                                                        accept={SupportedFileExtensionString}
                                                        maxNameLength={150}
                                                        url={ApiRoutes.job.postAttachments(
                                                            job.id || this.state.uploadSessionId,
                                                        )}
                                                        onUploadComplete={() => this.getAttachments()}
                                                        files={job.uploads || []}
                                                        onDownload={this.onDownloadAttachment}
                                                        onDelete={this.onRemoveAttachment}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    )}
                                    {/* Save Button */}

                                    <div className="d-flex justify-content-center mb-3 mt-4">
                                        <SaveButton
                                            saving={!!formState.saving}
                                            disabled={dispatchPortLoading}
                                            label={typeof job.id !== 'string' ? 'Update Job' : 'Save Job'}
                                        />

                                        {edit.allowDelete && (
                                            <Button
                                                type="button"
                                                color="danger"
                                                name="jobForm"
                                                className="ml-1"
                                                outline
                                                onClick={this.onDeleteButtonClick}
                                            >
                                                <FontAwesomeIcon
                                                    className="mr-1"
                                                    icon={faTrashAlt}
                                                />
                                                Delete Job
                                            </Button>
                                        )}
                                    </div>
                                </fieldset>
                            </form>
                        )}
                    </ModalBody>

                    {Boolean(slideOpen) && (
                        <>
                            <OrganizationContactForm
                                ref={this.contactFormRef}
                                show={showContactsForm}
                                readOnly={contactFormIsReadOnly}
                                allowContactTypeEdit={allowContactTypeEdit}
                                contact={selectedContact}
                                onClose={() => {
                                    this.setState({ showContactsForm: false });
                                }}
                                onChange={(e) => {
                                    const { selectedContact } = { ...this.state };
                                    selectedContact[e.target.name] = e.target.value;
                                    this.setState({
                                        newContact: { ...selectedContact },
                                    });
                                }}
                                onCheckedChanged={(e) => {
                                    const { selectedContact } = { ...this.state };
                                    selectedContact[e.target.name] = e.target.checked;
                                    this.setState({
                                        newContact: { ...selectedContact },
                                    });
                                }}
                                onContactTypeChanged={(items) => {
                                    const { selectedContact } = this.state;
                                    selectedContact.contactTypes = items.map(
                                        (x) => x.value,
                                    );
                                    this.setState({
                                        newContact: { ...selectedContact },
                                    });
                                }}
                                onSaveCallback={this.onOrganizationContactSaved}
                            />

                            <AddressFormNew
                                ref={this.addressFormRef}
                                onSaveCallback={this.onSaveAddress}
                                isUnionWork={contract?.isUnionWork}
                                countryId={contract?.countryId}
                                countryCode={contract?.countryCode}
                                contractId={contract?.id}
                                workTypeId={contract?.contractUnionWorkTypeId}
                                contractUsesPrevailingWage={contract?.prevailingWage || contract?.requirePaySchedule}
                                usCounties={usCounties}
                                timeZone={selectedDispatchLocation?.timeZone}
                            />
                        </>
                    )}
                </Modal>

                <Modal
                    isOpen={this.state.showApplyModal}
                    toggle={() => this.setState({
                        showApplyModal: false,
                        pendingUpdatedJob: null,
                        applyTargets: [],
                        formState: {
                            ...formState,
                            saving: false,
                        },
                    })}
                >
                    <ModalHeader>Apply changes</ModalHeader>
                    <ModalBody>
                        <p className="mb-2">
                            This job is part of a run. Do you want to update only this job, or the subsequent jobs in the run?
                        </p>
                        <div className="mb-2">
                            <strong>Will apply to these dates:</strong>
                            <ul className="mt-2">
                                {(this.state.applyTargets || []).map((t) => (
                                    <li key={t.id}>{moment(t.jobDate).format('ddd, MMM D, YYYY')}</li>
                                ))}
                                {!this.state.applyTargets?.length && <li>(No future jobs in this run)</li>}
                            </ul>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="secondary"
                            onClick={this.applyOnlyThisJob}
                            disabled={this.state.applyingSingleJob || this.state.applyingAllJobs}
                        >
                            {this.state.applyingSingleJob ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <Spinner
                                        size="sm"
                                        className="mr-2"
                                    />
                                    Applying...
                                </div>
                            ) : (
                                'Only this job'
                            )}
                        </Button>
                        <Button
                            color="primary"
                            onClick={this.applyAllJobsInRun}
                            disabled={!this.state.applyTargets?.length || this.state.applyingSingleJob || this.state.applyingAllJobs}
                        >
                            {this.state.applyingAllJobs ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <Spinner
                                        size="sm"
                                        className="mr-2"
                                    />
                                    Applying to all...
                                </div>
                            ) : (
                                'All jobs in this run'
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>
            </>
        );
    }
}

export default JobForm;
